import dayjs from "dayjs";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import ConfirmDialog from "../../../layouts/confirmDialog/confirmDialog.vue";
import ListItemNumberModel from "../../../models/listItemNumberModel";
import ListItemStringModel from "../../../models/listItemStringModel";
import ContractorDocumentRestModel from "../../../models/rest/contractor/contractorDocumentRestModel";
import ContractorPolicyPaymentConfirmationRestModel from "../../../models/rest/contractor/contractorPolicyPaymentConfirmationRestModel";
import ContractorPolicyRestModel from "../../../models/rest/contractor/contractorPolicyRestModel";
import VForm from "../../../models/v-form";

@Component({
    components: {
        "confirm-dialog": ConfirmDialog,
    }
})

export default class PolicyDialog extends Vue {
    policyTermLabel = this.$t("contractor.expiration");
    files: File[] = [];
    expandedDocuments: ContractorDocumentRestModel[] = [];
    confirmationFiles: File[] = [];
    confirmationTerm: string = "";

    policyDocumentListHeaders = [
        {
            text: this.$t("contractor.file"),
            align: "start",
            value: "name"
        },
        {
            text: this.$t("common.createdAt"),
            align: "start",
            value: "createdAt"
        },
        {
            text: this.$t("common.createdBy"),
            align: "start",
            value: "createdBy"
        },
        {
            text: this.$t("common.actions"),
            align: "right",
            value: "actions",
            width: "1%",
            sortable: false,
        }
    ];

    validation = {
        region: [(value: string) => value != undefined || this.$t("common.validationErrorRequired")],
        policyTerm: [(value: string) => !!value || this.$t("common.validationErrorRequired")],
        files: [
            (value: File[]) => {
                if (this.contractorPolicyIsTemporary) {
                    return (value && value.length > 0) || this.$t("common.validationErrorRequired");
                }
                return true;
            }
        ],
        confirmationTerm: [
            (value: string) => {
                if (this.confirmationFiles.length > 0) {
                    return !!value || this.$t("common.validationErrorRequired");
                }
                return true;
            }
        ],
    };

    get isLoading(): boolean {
        return this.$store.direct.state.loading;
    }

    get showDialog(): boolean {
        return this.$store.direct.state.contractor.showPolicyDialog;
    }

    get policy(): ContractorPolicyRestModel {
        const policy = this.$store.direct.state.contractor.policy;
        policy.policyTerm = this.formatSingleDateTimeValue(policy.policyTerm);
        return policy;
    }

    get selectedPolicyRegions(): ListItemNumberModel[] {
        const result: ListItemNumberModel[] = [];
        const regionNational = this.policyRegions.find(x => x.value == 0);
        const regionInternational = this.policyRegions.find(x => x.value == 1);
        const regionCabotage = this.policyRegions.find(x => x.value == 2);
        if (this.policy.isNational && regionNational != undefined)
            result.push(regionNational);
        if (this.policy.isInternational && regionInternational != undefined)
            result.push(regionInternational);
        if (this.policy.isCabotage && regionCabotage != undefined)
            result.push(regionCabotage);
        return result;
    }

    set selectedPolicyRegions(value: ListItemNumberModel[]) {
        this.policy.isNational = value.find(x => x.value == 0) != undefined;
        this.policy.isInternational = value.find(x => x.value == 1) != undefined;
        this.policy.isCabotage = value.find(x => x.value == 2) != undefined;
    }

    get policyDocumentList(): ContractorDocumentRestModel[] {
        return this.$store.direct.state.contractor.policyDocumentList;
    }

    get policyRegions(): ListItemNumberModel[] {
        return this.$store.direct.state.contractor.policyRegions;
    }

    get policyTypes(): ListItemStringModel[] {
        return this.$store.direct.state.contractor.policyTypes;
    }

    get contractorPolicyIsTemporary(): boolean {
        return this.$store.direct.state.contractor.contractorPolicyIsTemporary;
    }

    get showContractorPolicyPaymentConfirmationUploadDialog(): boolean {
        return this.$store.direct.state.contractor.showContractorPolicyPaymentConfirmationUploadDialog;
    }

    set showContractorPolicyPaymentConfirmationUploadDialog(value: boolean) {
        this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(value);
    }

    get allDocumentsHaveConfirmation(): boolean {
        if (this.policyDocumentList.length === 0) return true;
        return this.policyDocumentList.every(doc => doc.paymentConfirmations && doc.paymentConfirmations.length > 0);
    }

    formatSingleDateTimeValue(time: any) {
        return time != null ? dayjs(time).format("YYYY-MM-DD HH:mm") : "";
    }

    async policyDocumentDownloadClick(document: ContractorDocumentRestModel) {
        await this.$store.direct.dispatch.contractor.downloadPolicyDocument({ id: document.id });
    }

    @Watch("policyDocumentList", { immediate: true })
    onPolicyDocumentListChange(newList: ContractorDocumentRestModel[]) {
        this.expandedDocuments = [...newList];
    }

    async deleteFile(item: any) {
        await this.$store.direct.dispatch.confirm
            .openConfirmDialog({
                showConfirmDialog: true,
                title: this.$t("contractor.deleteFileTitle").toString(),
                message: this.$t("contractor.deletetFileMessage").toString() + item.name,
                links: [],
                options: { buttonColor: "error", layoutColor: "red" },
                buttonType: "yes/no"
            })
            .then(async confirm => {
                if (confirm) {
                    await this.$store.direct.dispatch.contractor.deletePolicyDocument({ id: item.id });
                }
            })
    }

    openPaymentConfirmationUpload(item: ContractorDocumentRestModel) {
        this.$store.direct.commit.contractor.setCurrentContractorPolicyDocumentFileId(item.id.toString());
        this.confirmationFiles = [];
        this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(true);
    }

    closePaymentConfirmationUpload() {
        this.confirmationFiles = [];
        this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(false);
    }

    formatDate(dateTime: any) {
        if (dateTime != null) {
            return dayjs(dateTime).format("YYYY-MM-DD");
        }
    }

    async savePaymentConfirmation() {
        if ((this.$refs.confirmationForm as VForm).validate()) {
            if (this.confirmationFiles.length > 0 && !this.confirmationTerm) {
                await this.showMissingConfirmationsDialog();
                return;
            }
            if (this.confirmationFiles.length > 0) {
                await this.$store.direct.dispatch.contractor.saveContractorPolicyPaymentConfirmation({ files: this.confirmationFiles, confirmationTerm: this.confirmationTerm });
                this.confirmationFiles = [];
                this.confirmationTerm = "";
                this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(false);
                await this.$store.direct.dispatch.contractor.loadPolicyDocumentList();
            }
        }
    }

    async downloadPaymentConfirmation(document: ContractorDocumentRestModel, confirmation: ContractorPolicyPaymentConfirmationRestModel) {
        await this.$store.direct.dispatch.contractor.downloadContractorPolicyPaymentConfirmation({
            confirmationId: confirmation.id,
            policyFileId: document.id.toString()
        });
    }

    async deletePaymentConfirmation(document: ContractorDocumentRestModel, confirmation: ContractorPolicyPaymentConfirmationRestModel) {
        await this.$store.direct.dispatch.confirm
            .openConfirmDialog({
                showConfirmDialog: true,
                title: this.$t("contractor.deleteFileTitle").toString(),
                message: this.$t("contractor.deletetFileMessage").toString() + confirmation.name,
                links: [],
                options: { buttonColor: "error", layoutColor: "red" },
                buttonType: "yes/no"
            })
            .then(async confirm => {
                if (confirm) {
                    await this.$store.direct.dispatch.contractor.deleteContractorPolicyPaymentConfirmation({
                        confirmationId: confirmation.id,
                        policyFileId: document.id.toString()
                    });
                    await this.$store.direct.dispatch.contractor.loadPolicyDocumentList();
                }
            });
    }

    async showMissingConfirmationsDialog(): Promise<void> {
        await this.$store.direct.dispatch.confirm.openConfirmDialog({
            showConfirmDialog: true,
            title: this.$t("contractor.missingConfirmationsTitle").toString(),
            message: this.$t("contractor.missingConfirmationsMessage").toString(),
            links: [],
            options: { buttonColor: "error", layoutColor: "red" },
            buttonType: "ok"
        });
    }

    async showMissingDocumentDialog(): Promise<void> {
        await this.$store.direct.dispatch.confirm.openConfirmDialog({
            showConfirmDialog: true,
            title: this.$t("contractor.missingDocumentTitle").toString(),
            message: this.$t("contractor.missingDocumentMessage").toString(),
            links: [],
            options: { buttonColor: "error", layoutColor: "red" },
            buttonType: "ok"
        });
    }


    async saveClick(): Promise<void> {
        if ((this.$refs.form as VForm).validate()) {
            if (this.policy.value.toString().length == 0) {
                this.policy.value = 0 as number;
            }

            // Validation: confirmation without term date
            if (this.confirmationFiles.length > 0 && !this.confirmationTerm) {
                await this.showMissingConfirmationsDialog();
                return;
            }

            // New policy: attachment required
            if (this.contractorPolicyIsTemporary && this.files.length > 0 && this.confirmationFiles.length === 0) {
                await this.showMissingConfirmationsDialog();
                return;
            }

            // Policy editing: no attachments in the database and no new files
            if (!this.contractorPolicyIsTemporary && this.policyDocumentList.length === 0 && this.files.length === 0) {
                await this.showMissingDocumentDialog();
                return;
            }

            // Policy editing: adds a new attachment but without confirmation
            if (!this.contractorPolicyIsTemporary && this.files.length > 0 && this.confirmationFiles.length === 0) {
                await this.showMissingConfirmationsDialog();
                return;
            }

            // Policy editing: existing attachments without confirmations
            if (!this.contractorPolicyIsTemporary && !this.allDocumentsHaveConfirmation) {
                await this.showMissingConfirmationsDialog();
                return;
            }

            await this.$store.direct.dispatch.contractor.savePolicy();

            if (this.files.length > 0) {
                await this.$store.direct.dispatch.contractor.savePolicyDocument({
                    policyFiles: this.files,
                    confirmationFiles: this.confirmationFiles,
                    confirmationTerm: this.confirmationTerm,
                });
            }

            this.files = [];
            this.confirmationFiles = [];
            this.confirmationTerm = "";
            this.expandedDocuments = [];
            this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(false);
            this.$store.direct.commit.contractor.setShowPolicyDialog(false);
            this.$store.direct.commit.contractor.setPolicy({} as ContractorPolicyRestModel);
            this.$store.direct.commit.contractor.setPolicyDocumentList([] as ContractorDocumentRestModel[]);
        }
    }

    closeClick(): void {
        (this.$refs.form as VForm).resetValidation();
        this.files = [];
        this.expandedDocuments = [];
        this.confirmationFiles = [];
        this.$store.direct.commit.contractor.setCurrentContractorPolicyDocumentFileId(null);
        this.$store.direct.commit.contractor.setShowContractorPolicyPaymentConfirmationUploadDialog(false);
        this.$store.direct.commit.contractor.setShowPolicyDialog(false);
        this.$store.direct.commit.contractor.setPolicy({} as ContractorPolicyRestModel);
        this.$store.direct.commit.contractor.setPolicyDocumentList([] as ContractorDocumentRestModel[]);
    }
}
